/**
 * Accessibility — Central Rentals' public accessibility statement.
 * Route: /legal/accessibility (and legacy alias /accessibility).
 *
 * Kept as a static, plain-language page so screen-readers and Google
 * both see the whole statement without JS.
 */
import { Link } from "react-router-dom";
import { Accessibility as AccessibilityIcon, Mail, Phone } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import { usePageTitle } from "@/lib/seo";

export default function Accessibility() {
  usePageTitle("Accessibility — Central Rentals Canada");

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="accessibility-page">
      <PublicHeader light />
      <section className="max-w-3xl mx-auto px-5 py-12 md:py-16">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-extrabold tracking-[0.18em] uppercase text-emerald-700">
          <AccessibilityIcon className="w-3.5 h-3.5"/> Accessibility statement
        </div>
        <h1 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
          Everyone should be able to use Central Rentals.
        </h1>
        <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
          Central Rentals Canada Inc. is committed to providing an inclusive experience for every landlord, tenant, and vendor — including people using screen-readers, keyboard-only navigation, magnifiers, and voice-control tools.
        </p>

        <div className="mt-10 space-y-8 text-slate-800 leading-relaxed">
          <div>
            <h2 className="font-display text-xl font-extrabold text-slate-900">Our commitment</h2>
            <p className="mt-2 text-sm">
              We build to <strong>WCAG 2.2 Level AA</strong> and comply with the <strong>Accessible Canada Act (ACA)</strong>, including its regulations and referenced technical standards. That means every screen we ship goes through automated axe-core scans, and every marketing update is reviewed against WCAG 2.2 AA before it lands in production.
            </p>
          </div>

          <div>
            <h2 className="font-display text-xl font-extrabold text-slate-900">What we support today</h2>
            <ul className="mt-2 text-sm space-y-1.5 list-disc pl-5">
              <li>Full keyboard navigation — Tab, Shift-Tab, Enter, Escape, and arrow keys everywhere.</li>
              <li>Screen-reader labels on every interactive element (buttons, forms, dialogs).</li>
              <li>Colour contrast that meets or exceeds a 4.5:1 ratio for body text and 3:1 for large text.</li>
              <li>Zoom to 200% without loss of content or function.</li>
              <li>Video/audio content ships with captions or transcripts.</li>
              <li>Form errors identify the field by name and explain how to fix them.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-xl font-extrabold text-slate-900">Ongoing improvements</h2>
            <p className="mt-2 text-sm">
              Accessibility is not a one-shot audit. Every quarter we re-audit the whole app with a mix of automated tooling (axe DevTools, Lighthouse) and manual screen-reader testing (VoiceOver on iOS/macOS, NVDA on Windows, TalkBack on Android). Bugs go on our public tracker and are prioritised alongside functional bugs.
            </p>
          </div>

          <div>
            <h2 className="font-display text-xl font-extrabold text-slate-900">Report an issue or request an alternative format</h2>
            <p className="mt-2 text-sm">
              If any part of Central Rentals is difficult or impossible for you to use, please tell us. We treat every accessibility report as a priority ticket and reply within one business day. We can also send any document (lease, notice, tax export) in an accessible alternative format on request — no charge.
            </p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a
                href="mailto:accessibility@centralrentals.ca?subject=Accessibility%20feedback"
                data-testid="a11y-email"
                className="group inline-flex items-center gap-2 p-4 rounded-xl border border-slate-200 hover:border-emerald-400 hover:shadow-md transition-all"
              >
                <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center flex-none"><Mail className="w-4 h-4"/></div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Email</div>
                  <div className="font-bold text-sm text-slate-900 truncate">accessibility@centralrentals.ca</div>
                </div>
              </a>
              <a
                href="tel:+17804052498"
                data-testid="a11y-phone"
                className="group inline-flex items-center gap-2 p-4 rounded-xl border border-slate-200 hover:border-emerald-400 hover:shadow-md transition-all"
              >
                <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center flex-none"><Phone className="w-4 h-4"/></div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Phone / TTY-relay</div>
                  <div className="font-bold text-sm text-slate-900">780-405-2498</div>
                </div>
              </a>
            </div>
          </div>

          <div>
            <h2 className="font-display text-xl font-extrabold text-slate-900">Formal complaint process</h2>
            <p className="mt-2 text-sm">
              If our response doesn&apos;t resolve your issue, you can file a complaint with the Accessibility Commissioner (<a href="https://www.chrc-ccdp.gc.ca/en/complaints/make-complaint" target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:text-emerald-900 underline underline-offset-4">chrc-ccdp.gc.ca</a>) under the Accessible Canada Act.
            </p>
          </div>
        </div>

        <div className="mt-12 text-xs text-slate-500">
          Last updated: <time dateTime="2026-08-27">August 27, 2026</time>{" "}
          · <Link to="/legal/privacy" className="underline hover:text-slate-700">Privacy Policy</Link>{" "}
          · <Link to="/legal/terms" className="underline hover:text-slate-700">Terms of Service</Link>
        </div>
      </section>
    </div>
  );
}
